import React, {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useContext,
  useRef,
  useState,
} from 'react';
import { Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ProjectContext from '../../context/ProjectContext';
import useProjectVersionHistory from '../../context/VersionHistoryContext';

const KEY_BACKSPACE = 'Backspace';
const KEY_DELETE = 'Delete';
const KEY_ENTER = 'Enter';
const KEY_TAB = 'Tab';
const KEY_F2 = 'F2';

const propTypes = {
  keys: PropTypes.string,
  charPress: PropTypes.string,
  stopEditing: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  colDef: PropTypes.oneOfType([PropTypes.object]),
};
const defaultProps = {
  value: '',
  keys: '',
  charPress: '',
  stopEditing: {},
  colDef: {},
};

const NumericEditor = memo(
  forwardRef((props, ref) => {
    const createInitialState = () => {
      let startValue;
      let highlightAllOnFocus = true;

      if (props.keys === KEY_BACKSPACE || props.keys === KEY_DELETE) {
        startValue = '';
      } else if (props.charPress) {
        startValue = props.charPress;
        highlightAllOnFocus = false;
      } else {
        startValue = props.value;
        if (props.keys === KEY_F2) {
          highlightAllOnFocus = false;
        }
      }

      return {
        value: startValue,
        highlightAllOnFocus,
      };
    };
    const { duration: sprintDuration } = useContext(ProjectContext);
    const { selectedListItem, showVersionHistoryBar, readOnlyMode } = useProjectVersionHistory();
    const initialState = createInitialState();
    const [value, setValue] = useState(initialState.value);
    const [highlightAllOnFocus, setHighlightAllOnFocus] = useState(
      initialState.highlightAllOnFocus,
    );
    const isHidden =
      showVersionHistoryBar &&
      readOnlyMode &&
      selectedListItem !== null &&
      selectedListItem !== undefined;
    const refInput = useRef(null);
    let textMessage = '';
    if (props.colDef?.headerName === 'Sprints') {
      if (sprintDuration !== null) {
        textMessage = (
          <>
            <span>Estimate In Sprints</span>
            <br />
            <span>
              In this project
              <b>{` 1 sprint equals ${sprintDuration} weeks.`}</b>
            </span>
          </>
        );
      } else {
        textMessage = 'Estimate In Sprints';
      }
    } else if (props.colDef?.headerName === 'Team Members') {
      textMessage = 'Add Team Members';
    }

    useEffect(() => {
      const eInput = refInput.current;
      eInput.focus();
      if (highlightAllOnFocus) {
        eInput.select();

        setHighlightAllOnFocus(false);
      } else {
        const length = eInput.value ? eInput.value.length : 0;
        if (length > 0) {
          eInput.setSelectionRange(length, length);
        }
      }
    }, []);

    const cancelBeforeStart = props.charPress && '1234567890'.indexOf(props.charPress) < 0;

    const isLeftOrRight = (event) => ['ArrowLeft', 'ArrowLeft'].indexOf(event.key) > -1;

    const isCharNumeric = (charStr) => !!/\d/.test(charStr);

    const isKeyPressedNumeric = (event) => {
      const charStr = event.key;
      return isCharNumeric(charStr);
    };

    const deleteOrBackspace = (event) => [KEY_DELETE, KEY_BACKSPACE].indexOf(event.key) > -1;

    const finishedEditingPressed = (event) => {
      const { key } = event;
      return key === KEY_ENTER || key === KEY_TAB;
    };

    const onKeyDown = (event) => {
      if (isLeftOrRight(event) || deleteOrBackspace(event)) {
        event.stopPropagation();
        return;
      }

      if (!finishedEditingPressed(event) && !isKeyPressedNumeric(event)) {
        if (event.preventDefault) event.preventDefault();
      }

      if (finishedEditingPressed(event)) {
        props.stopEditing();
      }
    };
    const handleChange = (event) => {
      let newValue = event.target.value;

      if (newValue.length > 2) {
        newValue = newValue.slice(0, 2);
      }
      if (newValue === '0') {
        newValue = '';
      }

      setValue(newValue);
    };
    useImperativeHandle(ref, () => ({
      getValue() {
        return value;
      },

      isCancelBeforeStart() {
        return cancelBeforeStart;
      },

      isCancelAfterEnd() {
        return value > 1000000;
      },
    }));

    return (
      <Tooltip
        key="tooltip"
        open
        componentsProps={{
          tooltip: {
            sx: {
              color: '#FFFFFF',
              background: '#344054',
              fontWeight: '500',
              fontSize: '12px',
              padding: '12px',
              lineHeight: '18px',
              borderRadius: '6px',
              marginTop: '9px !important',
            },
          },
          arrow: {
            sx: {
              color: '#344054',
              marginRight: '50%',
            },
          },
        }}
        title={textMessage}
        arrow
      >
        <div>
          {props?.colDef.headerName === 'Sprints' ? (
            <AccessTimeIcon className="icon" />
          ) : (
            <PeopleAltOutlinedIcon className="icon" />
          )}
          <input
            className="skills-drop-down"
            type="number"
            min={1}
            ref={refInput}
            value={value || ''}
            max={99}
            onChange={handleChange}
            onKeyDown={(event) => onKeyDown(event)}
            disabled={isHidden}
          />
        </div>
      </Tooltip>
    );
  }),
);

NumericEditor.propTypes = propTypes;
NumericEditor.defaultProps = defaultProps;
export default NumericEditor;
